import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import useNewUpload, {
  getStateFromAnalysedFile,
  type TNewUploadAnalysedFile,
} from './useNewUpload'
import { PrivateAPI } from '../utils/api'

type TRouteParams = {
  fileId: string
}

type TAnalysedFileResponse = {
  analysedFile: TNewUploadAnalysedFile
}

const useRestoreAnalysedFile = () => {
  const { fileId } = useParams<TRouteParams>()
  const { currentAnalysedFile, setNewUploadState } = useNewUpload(
    useShallow((state) => ({
      currentAnalysedFile: state.analysedFile,
      setNewUploadState: state.setState,
    })),
  )

  const missingAnalysedFile =
    Boolean(fileId) && currentAnalysedFile?.id !== fileId

  const analysedFileQuery = useQuery({
    queryKey: ['analysedFile', fileId],
    queryFn: async () => {
      const response = await PrivateAPI<TAnalysedFileResponse>({
        url: `/uploads/analyse/${fileId}`,
        method: 'get',
      })

      return response.data.analysedFile
    },
    enabled: missingAnalysedFile,
  })

  useEffect(() => {
    if (!analysedFileQuery.data) {
      return
    }

    if (currentAnalysedFile?.id === analysedFileQuery.data.id) {
      return
    }

    setNewUploadState(getStateFromAnalysedFile(analysedFileQuery.data))
  }, [analysedFileQuery.data, currentAnalysedFile?.id, setNewUploadState])

  const analysedFile = missingAnalysedFile
    ? analysedFileQuery.data || null
    : currentAnalysedFile

  return {
    analysedFile,
    isLoadingAnalysedFile: missingAnalysedFile && analysedFileQuery.isLoading,
  }
}

export default useRestoreAnalysedFile
